import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  ArrowForwardRounded,
  LockOutlined,
  MailOutlineRounded,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { Link as RouterLink, useLocation } from "react-router-dom";
import FlashMessage from "../../components/FlashMessage";
import useRegistration from "../../hooks/useRegistration";
import { isValidEmailAddress } from "../../utils/customFunction";
import { SplitAuthWrapper } from "../Wrapper";
import UserContext from "../../context/User";
import useStyles from "./styles";

// Toggle to true once Google OAuth (webClientID) is configured in admin.
const ENABLE_GOOGLE_LOGIN = false;

function Login() {
  const theme = useTheme();
  const classes = useStyles();
  const formRef = useRef(null);
  const location = useLocation();
  // Set when the stored token turned out to be expired/rejected, so we explain
  // why they are back on this screen instead of just saying "Welcome back".
  const { sessionExpired } = useContext(UserContext);

  const [mainError, setMainError] = useState({});
  const [emailError, setEmailError] = useState("");
  const [passError, setPassError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const {
    goolgeSuccess,
    authenticationFailure,
    loading,
    setLoading,
    loginButtonSetter,
    loginError,
    setLoginError,
    mutateLogin,
  } = useRegistration();

  useEffect(() => {
    if (loginError) {
      setMainError({ type: "error", message: loginError });
    }
  }, [loginError]);

  const clearErrors = () => {
    setEmailError("");
    setPassError("");
    setLoginError("");
    setMainError({});
  };

  const handleSignIn = () => {
    clearErrors();
    const emailValue = formRef.current["userEmail"].value.trim();
    const passValue = formRef.current["userPass"].value;
    let valid = true;

    if (!isValidEmailAddress(emailValue)) {
      setEmailError("Enter a valid email address");
      valid = false;
    }
    if (!passValue) {
      setPassError("Password is required");
      valid = false;
    }
    if (!valid) return;

    setLoading(true);
    mutateLogin({ email: emailValue, password: passValue, type: "default" });
  };

  const handleGoogleSuccess = (credentialResponse) => {
    loginButtonSetter("GOOGLE");
    setLoading(true);
    goolgeSuccess(credentialResponse);
  };

  const toggleSnackbar = useCallback(() => {
    setMainError({});
  }, []);

  return (
    <SplitAuthWrapper heading="Welcome back!" subheading="Sign in to continue">
      <FlashMessage
        open={Boolean(mainError.type)}
        severity={mainError.type}
        alertMessage={mainError.message}
        handleClose={toggleSnackbar}
      />

      {sessionExpired && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          <Typography variant="body2">
            Session expired. Please login again.
          </Typography>
        </Alert>
      )}

      {/* noValidate: our own inline errors replace the browser's bubbles.
          onSubmit makes Enter in either field sign in. */}
      <form
        ref={formRef}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          handleSignIn();
        }}
      >
        <Box className={classes.field}>
          <Box className={classes.labelRow}>
            <Typography
              component="label"
              htmlFor="login-email"
              className={classes.label}
            >
              Email
            </Typography>
          </Box>
          <TextField
            id="login-email"
            name="userEmail"
            type="email"
            autoComplete="email"
            fullWidth
            variant="outlined"
            error={Boolean(emailError)}
            helperText={emailError}
            FormHelperTextProps={{ role: emailError ? "alert" : undefined }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MailOutlineRounded />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box className={classes.field}>
          <Box className={classes.labelRow}>
            <Typography
              component="label"
              htmlFor="login-password"
              className={classes.label}
            >
              Password
            </Typography>
            <RouterLink
              to="/forgot-password"
              state={{ email: formRef.current?.["userEmail"]?.value }}
              className={`${classes.inkLink} ${classes.forgot}`}
            >
              Forgot password?
            </RouterLink>
          </Box>
          <TextField
            id="login-password"
            name="userPass"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            fullWidth
            variant="outlined"
            error={Boolean(passError)}
            helperText={passError}
            FormHelperTextProps={{ role: passError ? "alert" : undefined }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlined />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowPassword((v) => !v)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                    size="large"
                  >
                    {showPassword ? (
                      <Visibility color="primary" />
                    ) : (
                      <VisibilityOff color="primary" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disableElevation
          disabled={loading}
          className={classes.signIn}
        >
          {loading ? (
            <CircularProgress color="inherit" size={22} />
          ) : (
            <>
              SIGN IN
              <ArrowForwardRounded className={classes.arrow} />
            </>
          )}
        </Button>
      </form>

      {/* Google Login hidden until OAuth is configured in admin.
          Re-enable by flipping ENABLE_GOOGLE_LOGIN to true. */}
      {ENABLE_GOOGLE_LOGIN && (
        <>
          <Divider sx={{ my: 2 }}>
            <Typography variant="caption" sx={{ color: theme.palette.grey[500] }}>
              or
            </Typography>
          </Divider>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={authenticationFailure}
              width="320"
              text="continue_with"
              shape="rectangular"
              logo_alignment="center"
            />
          </Box>
        </>
      )}

      <Typography component="p" className={classes.switchRow}>
        New to RushmyFood?{" "}
        <RouterLink
          to="/new-login"
          state={{ from: location.state?.from }}
          className={classes.inkLink}
        >
          Create an account
        </RouterLink>
      </Typography>

      <Typography component="p" className={classes.legal}>
        By signing in, you agree to our{" "}
        <RouterLink to="/terms" className={classes.inkLink}>
          Terms
        </RouterLink>
        {" "}and{" "}
        <RouterLink to="/privacy" className={classes.inkLink}>
          Privacy Policy
        </RouterLink>
      </Typography>
    </SplitAuthWrapper>
  );
}

export default Login;
